/* eslint-disable react/jsx-closing-bracket-location */
/* eslint-disable react/jsx-indent */
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  CircularProgress,
  Paper,
  LinearProgress
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { useState } from 'react'
import { type AggregationLevel, type GSRPCapacityReportModel } from 'Pages/Reports/GSRPCapacityReport.types'
import { generateGSRPCapacityPDF } from 'Pages/Reports/GSRPCapacityReportPDF.generator'
import { calculateSummaryStats, aggregateReportData, getCapacityStatus, getUtilizationPercent } from 'Pages/Reports/GSRPCapacityReport.utils'

interface PDFViewProps {
  reportData: GSRPCapacityReportModel[]
}

export function GSRPCapacityReportPDFView (props: PDFViewProps): JSX.Element {
  const { reportData } = props
  const [aggregationLevel, setAggregationLevel] = useState<AggregationLevel>('facility')
  const [isGenerating, setIsGenerating] = useState(false)

  const summaryStats = calculateSummaryStats(reportData)

  // Extract metadata from first row
  const isdName = reportData[0]?.isdName ?? 'Unknown ISD'
  const timespanName = reportData[0]?.timespanName ?? 'Unknown Timespan'

  const handleDownloadPDF = (): void => {
    setIsGenerating(true)
    try {
      generateGSRPCapacityPDF(reportData, aggregationLevel, {
        isdName,
        timespanName
      })
    } finally {
      setIsGenerating(false)
    }
  }

  const aggregatedData = aggregateReportData(reportData, aggregationLevel)

  // Group data by provider for facility view
  const byProvider = aggregatedData.reduce<Record<string, typeof aggregatedData>>((acc, row) => {
    if (acc[row.providerName] === undefined) {
      acc[row.providerName] = []
    }
    acc[row.providerName].push(row)
    return acc
  }, {})

  return (
    <Box>
      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Detail level:
          </Typography>
          <ToggleButtonGroup
            value={aggregationLevel}
            exclusive
            onChange={(e, newLevel) => {
              if (newLevel !== null) {
                setAggregationLevel(newLevel)
              }
            }}
            size="small"
          >
            <ToggleButton value="facility">Facility</ToggleButton>
            <ToggleButton value="provider">Provider</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Button
          variant="contained"
          size="medium"
          startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          onClick={handleDownloadPDF}
          disabled={isGenerating}
          sx={{ bgcolor: '#4DA4A4', '&:hover': { bgcolor: '#3D8484' } }}
        >
          {isGenerating ? 'Generating...' : 'Download PDF Report'}
        </Button>
      </Box>

      {/* Page-width container for entire report */}
      <Box sx={{
        maxWidth: '8.5in',
        mx: 'auto',
        bgcolor: 'white',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        p: { xs: 2, sm: 3 },
        borderRadius: 1
      }}>
        {/* Report metadata cards */}
        <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 140, flex: '1 1 auto' }}>
          <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>ISD</Typography>
          <Typography variant="body1" sx={{ color: '#333', fontWeight: 'bold', mt: 0.5 }}>{isdName}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 140, flex: '1 1 auto' }}>
          <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>School Year</Typography>
          <Typography variant="body1" sx={{ color: '#333', fontWeight: 'bold', mt: 0.5 }}>{timespanName}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 140, flex: '1 1 auto' }}>
          <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>Generated</Typography>
          <Typography variant="body1" sx={{ color: '#333', fontWeight: 'bold', mt: 0.5 }}>{new Date().toLocaleDateString()}</Typography>
        </Box>
        <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 140, flex: '1 1 auto' }}>
          <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>Detail Level</Typography>
          <Typography variant="body1" sx={{ color: '#333', fontWeight: 'bold', mt: 0.5 }}>
            {aggregationLevel === 'provider' ? 'Provider' : 'Facility'}
          </Typography>
        </Box>
        </Box>

        {/* Summary statistics cards */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1.5, color: '#333', fontWeight: 'bold' }}>
            Summary Statistics
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 120, flex: '1 1 auto' }}>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>Total Capacity</Typography>
              <Typography variant="h5" sx={{ color: '#333', fontWeight: 'bold', mt: 0.5 }}>{summaryStats.totalCapacity}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 120, flex: '1 1 auto' }}>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>Active Seats</Typography>
              <Typography variant="h5" sx={{ color: '#333', fontWeight: 'bold', mt: 0.5 }}>{summaryStats.totalActiveSeats}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 120, flex: '1 1 auto' }}>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>Open Seats</Typography>
              <Typography variant="h5" sx={{ color: '#4DA4A4', fontWeight: 'bold', mt: 0.5 }}>{summaryStats.openCapacity}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 120, flex: '1 1 auto' }}>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>Utilization</Typography>
              <Typography variant="h5" sx={{ color: '#333', fontWeight: 'bold', mt: 0.5 }}>{summaryStats.utilizationPercent}%</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 120, flex: '1 1 auto' }}>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>Seeking Spots</Typography>
              <Typography variant="h5" sx={{ color: '#FF9800', fontWeight: 'bold', mt: 0.5 }}>{summaryStats.totalPending + summaryStats.totalWaitlisted}</Typography>
              <Typography variant="caption" sx={{ color: '#999', fontSize: '0.65rem' }}>(Pending + Waitlisted)</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 120, flex: '1 1 auto' }}>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>Providers</Typography>
              <Typography variant="h5" sx={{ color: '#333', fontWeight: 'bold', mt: 0.5 }}>{summaryStats.providerCount}</Typography>
            </Box>
            <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#FAFAFA', borderRadius: 1, minWidth: 120, flex: '1 1 auto' }}>
              <Typography variant="caption" sx={{ color: '#666', fontWeight: 'bold' }}>Facilities</Typography>
              <Typography variant="h5" sx={{ color: '#333', fontWeight: 'bold', mt: 0.5 }}>{summaryStats.facilityCount}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Data Preview */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 1.5, color: '#333', fontWeight: 'bold' }}>
            Report Preview
          </Typography>
          <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 2 }}>
            Color coding: <Box component="span" sx={{ color: '#4DA4A4', fontWeight: 'bold' }}>Green (available)</Box>, <Box component="span" sx={{ color: '#F9D423', fontWeight: 'bold' }}>Yellow (limited 80-99%)</Box>, <Box component="span" sx={{ color: '#E4536B', fontWeight: 'bold' }}>Red (full 100%+)</Box>
          </Typography>

          {aggregationLevel === 'provider'
            ? (
          // Provider-level cards
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {aggregatedData.map((row, idx) => {
              const openSeats = Math.max(0, row.totalAllocatedCapacity - row.totalActiveSeats)
              const statusInfo = getCapacityStatus(row.totalActiveSeats, row.totalAllocatedCapacity)
              const utilization = getUtilizationPercent(row.totalActiveSeats, row.totalAllocatedCapacity)
              const seeking = row.countPendingProcessing + row.countWaitlisted

              return (
                <Paper key={idx} sx={{ p: 1.5, boxShadow: 1, border: `2px solid ${statusInfo.color}` }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1, color: '#333', fontSize: '0.95rem' }}>
                    {row.providerName}
                  </Typography>

                  {/* Utilization bar */}
                  <Box sx={{ mb: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666', fontSize: '0.7rem' }}>
                        Utilization
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 'bold', color: statusInfo.color, fontSize: '0.7rem' }}>
                        {utilization}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(utilization, 100)}
                      sx={{
                        height: 8,
                        borderRadius: 1,
                        bgcolor: '#E1E1E1',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: statusInfo.color,
                          borderRadius: 1
                        }
                      }}
                    />
                  </Box>

                  {/* Stats grid */}
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                    <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#FAFAFA', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.65rem' }}>Capacity</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333', fontSize: '1rem' }}>{row.totalAllocatedCapacity}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#FAFAFA', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.65rem' }}>Active</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#333', fontSize: '1rem' }}>{row.totalActiveSeats}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#FAFAFA', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.65rem' }}>Open</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#4DA4A4', fontSize: '1rem' }}>{openSeats}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', p: 0.75, bgcolor: '#FAFAFA', borderRadius: 1 }}>
                      <Typography variant="caption" sx={{ color: '#666', display: 'block', fontSize: '0.65rem' }}>Seeking</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 'bold', color: '#FF9800', fontSize: '1rem' }}>{seeking}</Typography>
                    </Box>
                  </Box>
                </Paper>
              )
            })}
          </Box>
              )
            : (
          // Facility-level cards grouped by provider
          <Box>
            {Object.entries(byProvider).map(([providerName, facilities], providerIdx) => (
              <Box key={providerIdx} sx={{ mb: 2.5 }}>
                <Typography variant="body1" sx={{ mb: 1, color: '#333', fontWeight: 'bold', fontSize: '0.95rem' }}>
                  {providerName}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, pl: 1.5 }}>
                  {facilities.map((facility, idx) => {
                    const openSeats = Math.max(0, facility.totalAllocatedCapacity - facility.totalActiveSeats)
                    const statusInfo = getCapacityStatus(facility.totalActiveSeats, facility.totalAllocatedCapacity)
                    const utilization = getUtilizationPercent(facility.totalActiveSeats, facility.totalAllocatedCapacity)
                    const seeking = facility.countPendingProcessing + facility.countWaitlisted

                    return (
                      <Paper key={idx} sx={{ p: 1, boxShadow: 1, borderLeft: `3px solid ${statusInfo.color}` }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'nowrap' }}>
                          {/* Facility name and utilization */}
                          <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#333', mb: 0.25, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {facility.facilityName ?? ''}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Box sx={{ flex: 1, maxWidth: 150 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={Math.min(utilization, 100)}
                                  sx={{
                                    height: 5,
                                    borderRadius: 1,
                                    bgcolor: '#E1E1E1',
                                    '& .MuiLinearProgress-bar': {
                                      bgcolor: statusInfo.color,
                                      borderRadius: 1
                                    }
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" sx={{ fontWeight: 'bold', color: statusInfo.color, fontSize: '0.7rem', minWidth: 28 }}>
                                {utilization}%
                              </Typography>
                            </Box>
                          </Box>

                          {/* Stats */}
                          <Box sx={{ display: 'flex', gap: 1.5, flex: '0 0 auto' }}>
                            <Box sx={{ textAlign: 'center', minWidth: 38 }}>
                              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.6rem', display: 'block' }}>Cap</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{facility.totalAllocatedCapacity}</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center', minWidth: 38 }}>
                              <Typography variant="caption" sx={{ color: '#666', fontSize: '0.6rem', display: 'block' }}>Act</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', fontSize: '0.85rem' }}>{facility.totalActiveSeats}</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center', minWidth: 38 }}>
                              <Typography variant="caption" sx={{ color: '#4DA4A4', fontSize: '0.6rem', display: 'block' }}>Open</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4DA4A4', fontSize: '0.85rem' }}>{openSeats}</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center', minWidth: 38 }}>
                              <Typography variant="caption" sx={{ color: '#FF9800', fontSize: '0.6rem', display: 'block' }}>Seek</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#FF9800', fontSize: '0.85rem' }}>{seeking}</Typography>
                            </Box>
                          </Box>
                        </Box>
                      </Paper>
                    )
                  })}
                </Box>
              </Box>
            ))}
          </Box>
              )}
        </Box>
      </Box>
    </Box>
  )
}
